import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Empty, Space, Spin, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  FileDoneOutlined,
  FundOutlined,
  ReloadOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import api from '../services/api';
import './Dashboard.css';

const { Title, Text } = Typography;

interface ProviderDashboardSummary {
  totalOrders: number;
  paidOrders: number;
  todayNewOrders: number;
  weekNewOrders: number;
  todayServiceItems: number;
  pendingOrders: number;
  completedOrders: number;
  totalOrderAmount: number;
  monthOrderAmount: number;
  withdrawableAmount: number;
  settledFlowAmount: number;
  monthSettlementAmount: number;
  orderSettlementAmount: number;
  pendingOrderSettlementAmount: number;
  monthOrderSettlementAmount: number;
}

interface ProviderDashboardProfile {
  id: string;
  username: string;
  name: string;
  phone: string;
  orderSourceWechat: boolean;
  orderSourceMeituan: boolean;
  withdrawableAmount: number;
}

interface ProviderDashboardStatusItem {
  status: string;
  count: number;
}

interface ProviderDashboardTrendItem {
  date: string;
  orderCount: number;
  paidAmount: number;
}

interface ProviderDashboardRecentOrderItem {
  id: string;
  orderNo: string;
  source: string;
  productName: string;
  contactName: string;
  totalAmount: number;
  payStatus: string;
  status: string;
  createdAt: string;
  paidAt?: string;
}

interface ProviderDashboardSettlementFlowItem {
  id: string;
  amount: number;
  beforeAmount: number;
  afterAmount: number;
  operatorName: string;
  remark: string;
  invoiceUrl: string;
  settledAt?: string;
  createdAt: string;
}

interface ProviderDashboardOrderSettlementFlowItem {
  id: string;
  orderNo: string;
  orderItemNo: string;
  orderSource: string;
  meituanReservationNo: string;
  settlementType: string;
  settlementRatio: number;
  settlementAmountType: string;
  orderAmount: number;
  estimatedAmount: number;
  settlementAmount: number;
  settlementCycleDays: number;
  status: string;
  availableAt?: string;
  settledAt?: string;
  createdAt: string;
}

interface ProviderDashboardOverview {
  generatedAt: string;
  provider: ProviderDashboardProfile;
  summary: ProviderDashboardSummary;
  orderStatus: ProviderDashboardStatusItem[];
  orderTrend: ProviderDashboardTrendItem[];
  recentOrders: ProviderDashboardRecentOrderItem[];
  settlementFlows: ProviderDashboardSettlementFlowItem[];
  orderSettlementFlows: ProviderDashboardOrderSettlementFlowItem[];
}

const statusTextMap: Record<string, string> = {
  pending: '待派单',
  assigned: '待接单',
  waiting: '待服务',
  checkin_pending: '待签到',
  serving: '服务中',
  done: '已完成',
  accepted_review: '已评价',
  cancelled: '已取消',
  aftersale: '售后中',
  refunded: '已退款',
};

const statusColorMap: Record<string, string> = {
  pending: 'processing',
  assigned: 'warning',
  waiting: 'blue',
  checkin_pending: 'cyan',
  serving: 'purple',
  done: 'success',
  accepted_review: 'success',
  cancelled: 'default',
  aftersale: 'orange',
  refunded: 'magenta',
};

const sourceTextMap: Record<string, string> = {
  wechat: '微信',
  meituan: '美团',
};

const money = (value?: number) => `¥${Number(value || 0).toFixed(2)}`;

const orderSettlementStatusTextMap: Record<string, string> = {
  pending: '待结算',
  withdrawable: '已入账',
};

const orderSettlementStatusColorMap: Record<string, string> = {
  pending: 'warning',
  withdrawable: 'success',
};

const settlementTypeTextMap: Record<string, string> = {
  whole_order: '整单结算',
  sub_order: '子订单结算',
};

const settlementAmountTypeTextMap: Record<string, string> = {
  remaining_amount_ratio: '剩余金额比例',
  paid_amount_ratio: '支付金额比例',
};

const formatDateTime = (value?: string) => {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(
    date.getHours()
  ).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const ProviderDashboard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState<ProviderDashboardOverview | null>(null);

  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get<ProviderDashboardOverview>('/dashboard/provider-overview');
      setOverview(response.data);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '加载代理商工作台失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchOverview();
  }, [fetchOverview]);

  const summary = overview?.summary;

  const metricCards = useMemo(
    () => [
      {
        key: 'monthOrderAmount',
        title: '本月订单金额',
        value: money(summary?.monthOrderAmount),
        note: `已支付订单 ${summary?.paidOrders || 0} 单`,
        icon: <DollarOutlined />,
        accent: 'blue',
      },
      {
        key: 'withdrawableAmount',
        title: '当前可提现金额',
        value: money(summary?.withdrawableAmount),
        note: '代理商账户可提现余额',
        icon: <WalletOutlined />,
        accent: 'purple',
      },
      {
        key: 'pendingOrders',
        title: '进行中订单',
        value: `${summary?.pendingOrders || 0}`,
        note: '待派单、待接单、待服务和服务中',
        icon: <ShoppingCartOutlined />,
        accent: 'orange',
      },
      {
        key: 'todayServiceItems',
        title: '今日待服务',
        value: `${summary?.todayServiceItems || 0}`,
        note: '今日待签到或服务中的子订单',
        icon: <CalendarOutlined />,
        accent: 'cyan',
      },
      {
        key: 'completedOrders',
        title: '已完成订单',
        value: `${summary?.completedOrders || 0}`,
        note: `累计订单 ${summary?.totalOrders || 0} 单`,
        icon: <FileDoneOutlined />,
        accent: 'green',
      },
      {
        key: 'settledFlowAmount',
        title: '提现结算流水',
        value: money(summary?.settledFlowAmount),
        note: `本月结算 ${money(summary?.monthSettlementAmount)}`,
        icon: <FundOutlined />,
        accent: 'red',
      },
      {
        key: 'orderSettlementAmount',
        title: '订单结算入账',
        value: money(summary?.orderSettlementAmount),
        note: `待结算 ${money(summary?.pendingOrderSettlementAmount)} / 本月入账 ${money(summary?.monthOrderSettlementAmount)}`,
        icon: <WalletOutlined />,
        accent: 'green',
      },
    ],
    [summary]
  );

  const orderTrendOption = useMemo(() => {
    const trend = overview?.orderTrend || [];
    return {
      color: ['#2f6bff', '#7a5cff'],
      tooltip: { trigger: 'axis' },
      legend: { data: ['订单金额', '订单数量'], top: 0, right: 0 },
      grid: { left: 12, right: 12, top: 50, bottom: 10, containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: trend.map(item => item.date.slice(5)),
        axisLine: { lineStyle: { color: '#d6dfeb' } },
      },
      yAxis: [
        { type: 'value', name: '金额', splitLine: { lineStyle: { color: '#eef3f9' } } },
        { type: 'value', name: '单量', splitLine: { show: false } },
      ],
      series: [
        {
          name: '订单金额',
          type: 'line',
          smooth: true,
          symbolSize: 8,
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(47,107,255,0.24)' },
                { offset: 1, color: 'rgba(47,107,255,0.02)' },
              ],
            },
          },
          data: trend.map(item => Number(item.paidAmount || 0).toFixed(2)),
        },
        {
          name: '订单数量',
          type: 'bar',
          yAxisIndex: 1,
          barWidth: 18,
          itemStyle: { borderRadius: [8, 8, 0, 0] },
          data: trend.map(item => item.orderCount || 0),
        },
      ],
    };
  }, [overview]);

  const orderStatusOption = useMemo(() => {
    const statusItems = overview?.orderStatus || [];
    return {
      color: ['#2f6bff', '#ff9a3d', '#3cb179', '#7a5cff', '#ff5a6b', '#2dc5c4', '#8b98a9'],
      tooltip: { trigger: 'item' },
      legend: { bottom: 0, itemWidth: 10, itemHeight: 10 },
      series: [
        {
          type: 'pie',
          radius: ['52%', '74%'],
          center: ['50%', '44%'],
          label: { formatter: '{b}\n{c}', color: '#3b4656', fontSize: 12 },
          data: statusItems.map(item => ({
            name: statusTextMap[item.status] || item.status,
            value: item.count,
          })),
        },
      ],
    };
  }, [overview]);

  const orderColumns: ColumnsType<ProviderDashboardRecentOrderItem> = [
    { title: '订单号', dataIndex: 'orderNo', key: 'orderNo', width: 190, render: value => <span className="dashboard-order-no">{value}</span> },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 90,
      render: value => <Tag color={value === 'meituan' ? 'orange' : 'green'}>{sourceTextMap[value] || value || '--'}</Tag>,
    },
    { title: '服务项目', dataIndex: 'productName', key: 'productName', ellipsis: true, render: value => value || '--' },
    { title: '联系人', dataIndex: 'contactName', key: 'contactName', width: 100, render: value => value || '--' },
    { title: '金额', dataIndex: 'totalAmount', key: 'totalAmount', width: 110, render: value => <span className="dashboard-money">{money(value)}</span> },
    { title: '订单状态', dataIndex: 'status', key: 'status', width: 110, render: value => <Tag color={statusColorMap[value] || 'default'}>{statusTextMap[value] || value || '--'}</Tag> },
    { title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', width: 160, render: value => formatDateTime(value) },
  ];

  const flowColumns: ColumnsType<ProviderDashboardSettlementFlowItem> = [
    { title: '结算金额', dataIndex: 'amount', key: 'amount', width: 120, render: value => <span className="dashboard-money">{money(value)}</span> },
    { title: '结算后余额', dataIndex: 'afterAmount', key: 'afterAmount', width: 130, render: value => money(value) },
    { title: '操作人员', dataIndex: 'operatorName', key: 'operatorName', width: 120, render: value => value || '--' },
    { title: '备注', dataIndex: 'remark', key: 'remark', ellipsis: true, render: value => value || '--' },
    { title: '结算时间', dataIndex: 'settledAt', key: 'settledAt', width: 170, render: value => formatDateTime(value) },
  ];

  const orderSettlementFlowColumns: ColumnsType<ProviderDashboardOrderSettlementFlowItem> = [
    { title: '订单号', dataIndex: 'orderNo', key: 'orderNo', width: 170, render: value => <span className="dashboard-order-no">{value || '--'}</span> },
    { title: '子单号', dataIndex: 'orderItemNo', key: 'orderItemNo', width: 170, render: value => value || '--' },
    {
      title: '来源',
      dataIndex: 'orderSource',
      key: 'orderSource',
      width: 80,
      render: value => <Tag color={value === 'meituan' ? 'orange' : 'green'}>{sourceTextMap[value] || value || '--'}</Tag>,
    },
    {
      title: '结算类型',
      dataIndex: 'settlementType',
      key: 'settlementType',
      width: 110,
      render: value => settlementTypeTextMap[value] || value || '--',
    },
    {
      title: '金额类型',
      dataIndex: 'settlementAmountType',
      key: 'settlementAmountType',
      width: 130,
      render: value => settlementAmountTypeTextMap[value] || value || '--',
    },
    { title: '比例', dataIndex: 'settlementRatio', key: 'settlementRatio', width: 80, render: value => `${Number(value || 0).toFixed(2)}%` },
    { title: '订单金额', dataIndex: 'orderAmount', key: 'orderAmount', width: 110, render: value => money(value) },
    { title: '预估金额', dataIndex: 'estimatedAmount', key: 'estimatedAmount', width: 110, render: value => money(value) },
    { title: '结算金额', dataIndex: 'settlementAmount', key: 'settlementAmount', width: 120, render: value => <span className="dashboard-money">{money(value)}</span> },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: value => <Tag color={orderSettlementStatusColorMap[value] || 'default'}>{orderSettlementStatusTextMap[value] || value || '--'}</Tag>,
    },
    { title: '可结算时间', dataIndex: 'availableAt', key: 'availableAt', width: 160, render: value => formatDateTime(value) },
    { title: '入账时间', dataIndex: 'settledAt', key: 'settledAt', width: 160, render: value => formatDateTime(value) },
  ];

  return (
    <div className="dashboard-shell">
      <div className="dashboard-hero">
        <div className="dashboard-hero__content">
          <div className="dashboard-hero__eyebrow">
            <ShopOutlined />
            <span>代理商工作台</span>
          </div>
          <Title level={2} className="dashboard-hero__title">
            {overview?.provider?.name || '代理商'}的数据看板
          </Title>
          <Text className="dashboard-hero__desc">只展示当前代理商账号关联的订单数据和结算流水数据，不展示用户、产品、系统、退款审核等无关入口。</Text>
        </div>
        <div className="dashboard-hero__aside">
          <div className="dashboard-hero__tag">
            <ClockCircleOutlined />
            <span>数据时间 {formatDateTime(overview?.generatedAt)}</span>
          </div>
          <div className="dashboard-hero__tag">
            <ShopOutlined />
            <span>
              订单来源：
              {overview?.provider?.orderSourceWechat ? ' 微信' : ''}
              {overview?.provider?.orderSourceMeituan ? ' 美团' : ''}
              {!overview?.provider?.orderSourceWechat && !overview?.provider?.orderSourceMeituan ? ' 未配置' : ''}
            </span>
          </div>
          <Button type="primary" icon={<ReloadOutlined />} onClick={() => void fetchOverview()}>
            刷新数据
          </Button>
        </div>
      </div>

      <Spin spinning={loading}>
        {!overview ? (
          <Card className="dashboard-empty-card">
            <Empty description="暂无代理商工作台数据" />
          </Card>
        ) : (
          <>
            <div className="dashboard-metrics provider-dashboard-metrics">
              {metricCards.map(card => (
                <Card key={card.key} className={`dashboard-metric-card accent-${card.accent}`} variant="borderless">
                  <div className="dashboard-metric-card__icon">{card.icon}</div>
                  <div className="dashboard-metric-card__title">{card.title}</div>
                  <div className="dashboard-metric-card__value">{card.value}</div>
                  <div className="dashboard-metric-card__note">{card.note}</div>
                </Card>
              ))}
            </div>

            <div className="dashboard-grid dashboard-grid-main">
              <Card className="dashboard-panel dashboard-panel-wide" title={<Space><FundOutlined /><span>近 7 日订单趋势</span></Space>} extra={<Text type="secondary">当前代理商订单</Text>}>
                <ReactECharts option={orderTrendOption} style={{ height: 320 }} />
              </Card>
              <Card className="dashboard-panel" title={<Space><ShoppingCartOutlined /><span>订单状态分布</span></Space>}>
                <ReactECharts option={orderStatusOption} style={{ height: 320 }} />
              </Card>
            </div>

            <div className="dashboard-grid dashboard-grid-main">
              <Card className="dashboard-panel dashboard-table-panel" title={<Space><CalendarOutlined /><span>最新订单</span></Space>}>
                <Table<ProviderDashboardRecentOrderItem> rowKey="id" columns={orderColumns} dataSource={overview.recentOrders} pagination={false} scroll={{ x: 920 }} />
              </Card>
              <Card className="dashboard-panel dashboard-table-panel" title={<Space><WalletOutlined /><span>最近提现结算流水</span></Space>}>
                <Table<ProviderDashboardSettlementFlowItem> rowKey="id" columns={flowColumns} dataSource={overview.settlementFlows} pagination={false} scroll={{ x: 720 }} />
              </Card>
            </div>

            <Card className="dashboard-panel dashboard-table-panel" title={<Space><FundOutlined /><span>订单结算流水</span></Space>} extra={<Text type="secondary">按代理商结算配置自动生成</Text>}>
              <Table<ProviderDashboardOrderSettlementFlowItem>
                rowKey="id"
                columns={orderSettlementFlowColumns}
                dataSource={overview.orderSettlementFlows || []}
                pagination={false}
                scroll={{ x: 1480 }}
              />
            </Card>
          </>
        )}
      </Spin>
    </div>
  );
};

export default ProviderDashboard;
